// client/src/coms/views/canvas_parts/canvas_room_view_parts/brushImport/brushSchema.js
//
// The universal Voxemera brush preset. One record shape represents brushes
// from Procreate, Krita, MyPaint, Photoshop, GIMP and Voxemera itself:
//
//   source         where it came from, the original settings verbatim
//                  (`raw`) and the original preset text when small enough,
//                  so presets can be re-translated by a later importer.
//   assets         content-hash references to tip and grain images. The
//                  pixels live once in the asset store however many
//                  presets share them.
//   settings       normalised static behaviour (spacing, opacity, flow...).
//   dynamics       normalised input responses (pressure, tilt, jitter...).
//   texture        grain/pattern applied through the stroke.
//   rendering      how dabs combine (stroke-capped glaze or build-up).
//   engine         which Voxemera engine renders it, plus engine params.
//   compatibility  honest status with lists of what is implemented,
//                  approximated and missing.
//
// Version 1 records (the flat session presets stored before this schema)
// are migrated by `migrateLegacyBrushRecord`; the old record is kept so a
// failed migration never loses a brush.

export const BRUSH_SCHEMA_ID = 'voxemera.brush';
export const BRUSH_SCHEMA_VERSION = 2;

export const COMPATIBILITY = Object.freeze({
  FULL: 'full',
  HIGH: 'high',
  PARTIAL: 'partial',
  UNSUPPORTED: 'unsupported',
});

export const COMPATIBILITY_LABELS = Object.freeze({
  full: 'Fully supported',
  high: 'High compatibility',
  partial: 'Partial compatibility',
  unsupported: 'Unsupported',
});

export const RENDER_ENGINES = Object.freeze(['stamp', 'unsupported']);

const MAX_RAW_JSON_BYTES = 192 * 1024;
const MAX_SOURCE_TEXT_BYTES = 256 * 1024;

export function finiteNumber(value, fallback, min = -Infinity, max = Infinity) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, n));
}

function bool(value, fallback = false) {
  return typeof value === 'boolean' ? value : fallback;
}

function text(value, maxLength = 200, fallback = '') {
  if (value == null) return fallback;
  // eslint-disable-next-line no-control-regex
  return String(value).replace(/[\u0000-\u001f\u007f]/g, ' ').trim().slice(0, maxLength) || fallback;
}

export function sanitiseCurve(points) {
  if (!Array.isArray(points)) return [];
  const out = [];
  for (const point of points.slice(0, 128)) {
    const x = finiteNumber(point && point.x, NaN, 0, 1);
    const y = finiteNumber(point && point.y, NaN, 0, 1);
    if (Number.isFinite(x) && Number.isFinite(y)) out.push({ x, y });
  }
  out.sort((a, b) => a.x - b.x);
  return out.length >= 2 ? out : [];
}

function response(value, defaults = {}) {
  const source = value && typeof value === 'object' ? value : {};
  return {
    amount: finiteNumber(source.amount, defaults.amount ?? 0, 0, 1),
    curve: sanitiseCurve(source.curve),
  };
}

function tiltResponse(value) {
  const source = value && typeof value === 'object' ? value : {};
  return {
    amount: finiteNumber(source.amount, 0, -1, 1),
    angle: finiteNumber(source.angle, 0, 0, Math.PI / 2),
  };
}

function channel(value) {
  const source = value && typeof value === 'object' ? value : {};
  return {
    pressure: response(source.pressure),
    tilt: tiltResponse(source.tilt),
    speed: finiteNumber(source.speed, 0, -1, 1),
    jitter: finiteNumber(source.jitter, 0, 0, 1),
    minimum: finiteNumber(source.minimum, 0, 0, 1),
  };
}

function colourJitter(value) {
  const source = value && typeof value === 'object' ? value : {};
  return {
    hue: finiteNumber(source.hue, 0, 0, 1),
    saturation: finiteNumber(source.saturation, 0, 0, 1),
    lightness: finiteNumber(source.lightness, 0, 0, 1),
    darkness: finiteNumber(source.darkness, 0, 0, 1),
  };
}

// Strip anything that cannot round-trip through IndexedDB/JSON and cap the
// size, so hostile metadata cannot bloat the library.
export function toPlainJson(value, maxBytes = MAX_RAW_JSON_BYTES) {
  if (value == null) return null;
  const seen = new WeakSet();
  let json = '';
  try {
    json = JSON.stringify(value, (key, item) => {
      if (key === '__proto__' || key === 'constructor' || key === 'prototype') return undefined;
      if (item instanceof Uint8Array || item instanceof ArrayBuffer) return { $bytes: item.byteLength || item.length || 0 };
      if (typeof item === 'number' && !Number.isFinite(item)) return null;
      if (typeof item === 'bigint') return Number(item);
      if (item && typeof item === 'object') {
        if (seen.has(item)) return undefined;
        seen.add(item);
        if (typeof HTMLCanvasElement !== 'undefined' && item instanceof HTMLCanvasElement) return undefined;
      }
      if (typeof item === 'string' && item.length > 4096) return item.slice(0, 4096) + '…';
      return item;
    });
  } catch {
    return null;
  }
  if (!json || json.length > maxBytes) return json ? { $truncated: true, bytes: json.length } : null;
  return JSON.parse(json);
}

export function createEmptyBrushRecord(overrides = {}) {
  return normaliseBrushRecord({
    schema: BRUSH_SCHEMA_ID,
    schemaVersion: BRUSH_SCHEMA_VERSION,
    ...overrides,
  });
}

export function normaliseBrushRecord(input) {
  const record = input && typeof input === 'object' ? input : {};
  const source = record.source && typeof record.source === 'object' ? record.source : {};
  const assets = record.assets && typeof record.assets === 'object' ? record.assets : {};
  const settings = record.settings && typeof record.settings === 'object' ? record.settings : {};
  const dynamics = record.dynamics && typeof record.dynamics === 'object' ? record.dynamics : {};
  const rotation = dynamics.rotation && typeof dynamics.rotation === 'object' ? dynamics.rotation : {};
  const scatter = dynamics.scatter && typeof dynamics.scatter === 'object' ? dynamics.scatter : {};
  const count = dynamics.count && typeof dynamics.count === 'object' ? dynamics.count : {};
  const taper = dynamics.taper && typeof dynamics.taper === 'object' ? dynamics.taper : {};
  const colour = dynamics.colour && typeof dynamics.colour === 'object' ? dynamics.colour : {};
  const texture = record.texture && typeof record.texture === 'object' ? record.texture : null;
  const rendering = record.rendering && typeof record.rendering === 'object' ? record.rendering : {};
  const engine = record.engine && typeof record.engine === 'object' ? record.engine : {};
  const compatibility = record.compatibility && typeof record.compatibility === 'object' ? record.compatibility : {};
  const library = record.library && typeof record.library === 'object' ? record.library : {};
  const now = Date.now();

  const tips = (Array.isArray(assets.tips) ? assets.tips : [])
    .slice(0, 256)
    .map((tip) => ({
      hash: text(tip && tip.hash, 128),
      width: finiteNumber(tip && tip.width, 0, 0, 8192),
      height: finiteNumber(tip && tip.height, 0, 0, 8192),
    }))
    .filter((tip) => tip.hash);

  const status = Object.values(COMPATIBILITY).includes(compatibility.status) ? compatibility.status : COMPATIBILITY.PARTIAL;

  return {
    schema: BRUSH_SCHEMA_ID,
    schemaVersion: BRUSH_SCHEMA_VERSION,
    id: text(record.id, 120) || 'brush-' + now.toString(36) + '-' + Math.random().toString(36).slice(2, 10),
    name: text(record.name, 160, 'Imported brush'),
    createdAt: finiteNumber(record.createdAt, now, 0),
    updatedAt: finiteNumber(record.updatedAt, now, 0),
    library: {
      folder: text(library.folder, 160, 'Imported'),
      category: text(library.category, 60, 'Imported'),
      favourite: bool(library.favourite),
      order: finiteNumber(library.order, 0, 0, 1e9),
      private: library.private !== false,
      deletedAt: finiteNumber(library.deletedAt, 0, 0),
      lastUsedAt: finiteNumber(library.lastUsedAt, 0, 0),
    },
    source: {
      app: text(source.app, 40, 'unknown'),
      format: text(source.format, 40, 'unknown'),
      engine: text(source.engine, 80, ''),
      packName: text(source.packName, 160, ''),
      folderPath: text(source.folderPath, 512, ''),
      entryPath: text(source.entryPath, 512, ''),
      fileName: text(source.fileName, 260, ''),
      contentHash: text(source.contentHash, 128, ''),
      author: text(source.author, 160, ''),
      license: text(source.license, 200, ''),
      raw: toPlainJson(source.raw),
      sourceText: typeof source.sourceText === 'string' && source.sourceText.length <= MAX_SOURCE_TEXT_BYTES ? source.sourceText : '',
      importerVersion: finiteNumber(source.importerVersion, 1, 0, 1e6),
    },
    assets: {
      tips,
      tipSelection: ['sequential', 'random', 'pressure', 'angle', 'single'].includes(assets.tipSelection) ? assets.tipSelection : 'single',
      grain: assets.grain && typeof assets.grain === 'object' && assets.grain.hash
        ? { hash: text(assets.grain.hash, 128), width: finiteNumber(assets.grain.width, 0, 0, 8192), height: finiteNumber(assets.grain.height, 0, 0, 8192) }
        : null,
      thumbnail: assets.thumbnail && typeof assets.thumbnail === 'object' && assets.thumbnail.hash ? { hash: text(assets.thumbnail.hash, 128) } : null,
      preview: assets.preview && typeof assets.preview === 'object' && assets.preview.hash ? { hash: text(assets.preview.hash, 128) } : null,
    },
    settings: {
      spacing: finiteNumber(settings.spacing, 0.1, 0.002, 4),
      spacingJitter: finiteNumber(settings.spacingJitter, 0, 0, 2),
      tipScale: finiteNumber(settings.tipScale, 1, 0.02, 8),
      angle: finiteNumber(settings.angle, 0, -Math.PI * 4, Math.PI * 4),
      roundness: finiteNumber(settings.roundness, 1, 0.02, 1),
      flipX: bool(settings.flipX),
      flipY: bool(settings.flipY),
      opacity: finiteNumber(settings.opacity, 1, 0.01, 1),
      flow: finiteNumber(settings.flow, 1, 0.005, 1),
      opacityMode: settings.opacityMode === 'dab' ? 'dab' : 'stroke',
      eraser: bool(settings.eraser),
      edgeMode: settings.edgeMode === 'hard' ? 'hard' : 'normal',
      tipPolarity: ['light', 'dark', 'alpha'].includes(settings.tipPolarity) ? settings.tipPolarity : 'alpha',
      tipColour: settings.tipColour === 'image' ? 'image' : 'mask',
      suggestedSize: finiteNumber(settings.suggestedSize, 0, 0, 4000),
    },
    dynamics: {
      size: channel(dynamics.size),
      opacity: channel(dynamics.opacity),
      flow: channel(dynamics.flow),
      spacing: { pressure: response(dynamics.spacing && dynamics.spacing.pressure) },
      rotation: {
        follow: finiteNumber(rotation.follow, 0, -1, 1),
        jitter: finiteNumber(rotation.jitter, 0, 0, Math.PI * 2),
        randomPerStroke: bool(rotation.randomPerStroke),
        azimuth: bool(rotation.azimuth),
        pressure: finiteNumber(rotation.pressure, 0, -Math.PI * 2, Math.PI * 2),
        flipXJitter: bool(rotation.flipXJitter),
        flipYJitter: bool(rotation.flipYJitter),
      },
      scatter: {
        perpendicular: finiteNumber(scatter.perpendicular, 0, 0, 8),
        along: finiteNumber(scatter.along, 0, 0, 8),
        pressure: bool(scatter.pressure),
      },
      count: {
        value: Math.round(finiteNumber(count.value, 1, 1, 16)),
        jitter: finiteNumber(count.jitter, 0, 0, 1),
      },
      taper: {
        start: finiteNumber(taper.start, 0, 0, 1),
        end: finiteNumber(taper.end, 0, 0, 1),
        size: finiteNumber(taper.size, 0, 0, 1),
        opacity: finiteNumber(taper.opacity, 0, 0, 1),
        pressure: finiteNumber(taper.pressure, 0, 0, 1),
      },
      colour: {
        perDab: colourJitter(colour.perDab),
        perStroke: colourJitter(colour.perStroke),
      },
    },
    texture: texture && texture.hash
      ? {
          hash: text(texture.hash, 128),
          mode: texture.mode === 'moving' ? 'moving' : 'texturized',
          movement: finiteNumber(texture.movement, 0, 0, 1),
          scale: finiteNumber(texture.scale, 1, 0.01, 16),
          depth: finiteNumber(texture.depth, 1, 0, 1),
          depthMinimum: finiteNumber(texture.depthMinimum, 0, 0, 1),
          invert: bool(texture.invert),
          brightness: finiteNumber(texture.brightness, 0, -1, 1),
          contrast: finiteNumber(texture.contrast, 0, -1, 4),
          rotation: finiteNumber(texture.rotation, 0, -Math.PI * 4, Math.PI * 4),
          blend: texture.blend === 'subtract' ? 'subtract' : 'multiply',
          offsetJitter: bool(texture.offsetJitter),
        }
      : null,
    rendering: {
      mode: ['glaze', 'buildup', 'wet-mix', 'smudge'].includes(rendering.mode) ? rendering.mode : 'glaze',
      wetEdges: finiteNumber(rendering.wetEdges, 0, 0, 1),
      burntEdges: finiteNumber(rendering.burntEdges, 0, 0, 1),
      blendMode: text(rendering.blendMode, 40, 'normal'),
    },
    engine: {
      id: RENDER_ENGINES.includes(engine.id) ? engine.id : 'stamp',
      params: toPlainJson(engine.params, 32 * 1024) || {},
    },
    compatibility: {
      status,
      score: finiteNumber(compatibility.score, 0, 0, 100),
      validatedAgainstReference: compatibility.validatedAgainstReference === true,
      implemented: (Array.isArray(compatibility.implemented) ? compatibility.implemented : []).slice(0, 80).map((item) => text(item, 160)).filter(Boolean),
      approximated: (Array.isArray(compatibility.approximated) ? compatibility.approximated : []).slice(0, 80).map((item) => text(item, 200)).filter(Boolean),
      missing: (Array.isArray(compatibility.missing) ? compatibility.missing : []).slice(0, 80).map((item) => text(item, 200)).filter(Boolean),
      notes: (Array.isArray(compatibility.notes) ? compatibility.notes : []).slice(0, 40).map((item) => text(item, 300)).filter(Boolean),
    },
  };
}

// Feature reports from translators become a status. `major` missing
// features (a different paint engine, smudge, wet mixing, dual brushes)
// cap the status at partial; an unrenderable engine is unsupported. Only a
// comparison against reference output from the source application may
// promote a preset to fully supported.
export function scoreCompatibility(report = {}) {
  const implemented = Array.from(new Set(report.implemented || []));
  const approximated = Array.from(new Set(report.approximated || []));
  const missing = Array.from(new Set((report.missing || []).map((item) => (typeof item === 'string' ? { label: item, major: false } : item)).map((item) => JSON.stringify(item)))).map((item) => JSON.parse(item));
  const majorMissing = missing.filter((item) => item.major);
  let score = 100 - approximated.length * 4 - (missing.length - majorMissing.length) * 7 - majorMissing.length * 22;
  score = Math.max(0, Math.min(100, Math.round(score)));
  let status;
  if (report.unrenderable) {
    status = COMPATIBILITY.UNSUPPORTED;
    score = 0;
  } else if (majorMissing.length || score < 70) {
    status = COMPATIBILITY.PARTIAL;
  } else if (report.validatedAgainstReference === true && !approximated.length && !missing.length) {
    status = COMPATIBILITY.FULL;
  } else {
    status = COMPATIBILITY.HIGH;
  }
  return {
    status,
    score,
    validatedAgainstReference: report.validatedAgainstReference === true,
    implemented,
    approximated,
    missing: missing.map((item) => item.label + (item.major ? ' (major)' : '')),
    notes: report.notes || [],
  };
}

export function isPaintableRecord(record) {
  return !!record && record.engine && record.engine.id === 'stamp'
    && record.compatibility && record.compatibility.status !== COMPATIBILITY.UNSUPPORTED
    && record.assets && Array.isArray(record.assets.tips) && record.assets.tips.length > 0;
}

// Version-1 records are the flat runtime presets serialised by the old
// session library (with data-URL canvases). They migrate to schema v2 with
// the settings they had; their images are re-hashed into the asset store by
// the library, which passes the hashes in.
export function migrateLegacyBrushRecord(legacy, { tipHashes = [], grainHash = '' } = {}) {
  const old = legacy && typeof legacy === 'object' ? legacy : {};
  const fromKrita = !!old.kritaPreset;
  const fromProcreate = !!old.procreatePreset;
  const curve = (points) => sanitiseCurve(points);
  return normaliseBrushRecord({
    id: old.id,
    name: old.name,
    library: { folder: old.category || 'Imported', category: old.category || 'Imported' },
    source: {
      app: fromKrita ? 'krita' : fromProcreate ? 'procreate' : 'image',
      format: fromKrita ? 'kpp' : fromProcreate ? 'brush' : 'image',
      engine: fromKrita ? String((old.kritaPreset && old.kritaPreset.paintopid) || 'paintbrush') : fromProcreate ? 'procreate.silica' : 'stamp',
      fileName: old.sourcePath || '',
      raw: { legacyV1: toPlainJson({ ...old, kritaPreset: undefined, procreatePreset: undefined, originalTipCanvas: undefined, originalTipCanvases: undefined, originalGrainCanvas: undefined, originalTipCanvasDataUrl: undefined, originalTipCanvasesDataUrls: undefined, originalGrainCanvasDataUrl: undefined, originalTipDataUrl: undefined, thumbnailDataUrl: undefined }) },
    },
    assets: {
      tips: tipHashes.map((hash) => ({ hash })),
      tipSelection: tipHashes.length > 1 ? 'random' : 'single',
      grain: grainHash ? { hash: grainHash } : null,
    },
    settings: {
      spacing: old.spacing,
      tipScale: old.tipScale,
      angle: old.fixedAngle,
      opacity: old.brushOpacity,
      flow: old.flow,
      opacityMode: 'dab',
      edgeMode: old.edgeMode,
      tipPolarity: 'alpha',
    },
    dynamics: {
      size: { pressure: { amount: old.pressureToSize === false ? 0 : 1, curve: curve(old.sizeCurve) } },
      opacity: { pressure: { amount: old.pressureToOpacity ? 1 : 0, curve: curve(old.opacityCurve) } },
      flow: { pressure: { amount: old.pressureToFlow ? 1 : 0, curve: curve(old.flowCurve) } },
      spacing: { pressure: { amount: old.pressureToSpacing ? 1 : 0, curve: curve(old.spacingCurve) } },
      rotation: {
        follow: old.rotationMode === 'stroke' ? 1 : 0,
        jitter: old.rotationMode === 'random' ? Math.PI * 2 : 0,
        azimuth: old.rotationMode === 'tilt',
      },
      scatter: { perpendicular: old.scatter, pressure: true },
      count: { value: old.procreateShapeCount, jitter: old.procreateShapeCountJitter },
    },
    texture: grainHash
      ? { hash: grainHash, mode: 'moving', movement: 1, scale: old.procreateTextureScale, depth: old.procreateTextureDepth, invert: old.procreateTextureInverted }
      : null,
    rendering: { mode: 'buildup' },
    engine: { id: tipHashes.length ? 'stamp' : 'unsupported' },
    compatibility: {
      status: tipHashes.length ? COMPATIBILITY.PARTIAL : COMPATIBILITY.UNSUPPORTED,
      notes: ['Migrated from the version 1 session library. Re-import the original pack to apply the current translator.'],
    },
  });
}
